import { useEffect, useState } from "react";

import { PaperClipIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { useFormik } from "formik";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { LayersControl, MapContainer, Marker, TileLayer, useMapEvents, WMSTileLayer } from "react-leaflet";
import { trackPromise } from "react-promise-tracker";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import sortBy from "sort-by";
import * as Yup from "yup";

import { mapLayers } from "@/components/map/layers";
import { DatePicker } from "@/components/ui/datepicker";
import { getStrapiClient } from "@/lib/strapiClient";
import { cn } from "@/lib/utils";
import type { Accident, AccidentPost } from "@/models/accident";
import type { Accuracy } from "@/models/accuracy";
import type { Activitat } from "@/models/activitat";
import type { Cota } from "@/models/cota";
import type { Country } from "@/models/country";
import type { DangerDegree } from "@/models/danger_degree";
import type { Desencadenant } from "@/models/desencadentant";
import type { File as BEFile } from "@/models/file";
import type { Massis } from "@/models/massis";
import type { Material } from "@/models/material";
import type { Orientation } from "@/models/orientation";
import type { Origen } from "@/models/origen";
import type { Progressio } from "@/models/progressio";
import type { Region } from "@/models/region";
import type { Scope } from "@/models/scope";
import type { Season } from "@/models/season";
import type { SnowType } from "@/models/snowType";
import type { RootState } from "@/redux/store";

type Props = {
	edit?: boolean;
};

export default function AccidentsForm({ edit }: Props) {
	const { t } = useTranslation();
	const { accident_id } = useParams();
	const navigate = useNavigate();
	const userState = useSelector((state: RootState) => state.user);

	// External manage data
	const [marker, setMarker] = useState({ latitude: 42.70665956351041, longitude: 0.8679199218750001 });
	const [latitude, setLatitude] = useState(marker.latitude);
	const [longitude, setLongitude] = useState(marker.longitude);
	const [accident, setAccident] = useState<Accident>();
	const [photos, setPhotos] = useState<(File | BEFile)[]>([]);
	const [docs, setDocs] = useState<(File | BEFile)[]>([]);
	const [loadingFields, setLoadingFields] = useState(true);
	const [loadingAccident, setLoadingAccident] = useState(!!edit);

	// Form values
	const [temporadas, setTemporadas] = useState<Season[]>([]);
	const [scopes, setScopes] = useState<Scope[]>([]);
	const [accuracies, setAccuracies] = useState<Accuracy[]>([]);
	const [countries, setCountries] = useState<Country[]>([]);
	const [dangerDegrees, setDangerDegrees] = useState<DangerDegree[]>([]);
	const [desencadenants, setDesencadenants] = useState<Desencadenant[]>([]);
	const [cotas, setCotas] = useState<Cota[]>([]);
	const [activitats, setActivitats] = useState<Activitat[]>([]);
	const [regions, setRegions] = useState<Region[]>([]);
	const [massis, setMassis] = useState<Massis[]>([]);
	const [origens, setOrigens] = useState<Origen[]>([]);
	const [snowType, setSnowType] = useState<SnowType[]>([]);
	const [orientation, setOrientation] = useState<Orientation[]>([]);
	const [progression, setProgression] = useState<Progressio[]>([]);
	const [material, setMaterial] = useState<Material[]>([]);

	const MyMap = () => {
		useMapEvents({
			click: (e) => {
				setMarker({
					latitude: e.latlng.lat,
					longitude: e.latlng.lng,
				});

				setLatitude(e.latlng.lat);
				setLongitude(e.latlng.lng);
			},
		});
		return null;
	};

	useEffect(() => {
		if (!userState.loggedIn) {
			navigate("/");
		}
	}, [userState.loggedIn, navigate]);

	/* ---------------- */

	useEffect(() => {
		window.scrollTo(0, 0);
		const getAllFields = async () => {
			try {
				const client = getStrapiClient();
				const options = { populate: "*" as const };
				const cast = <T,>(data: unknown) => data as unknown as T[];

				const [
					accuraciesRes,
					scopesRes,
					countriesRes,
					dangerDegreesRes,
					desencadenantsRes,
					cotasRes,
					activitatsRes,
					regionsRes,
					massisRes,
					origensRes,
					snowTypesRes,
					orientationsRes,
					progressionRes,
					materialRes,
					seasonsRes,
				] = await Promise.all([
					client.collection("accuracies").find(options),
					client.collection("scopes").find(options),
					client.collection("countries").find(options),
					client.collection("danger-degrees").find(options),
					client.collection("desencadenants").find(options),
					client.collection("cotas").find(options),
					client.collection("activitats").find(options),
					client.collection("regions").find(options),
					client.collection("massis").find(options),
					client.collection("origens").find(options),
					client.collection("snow-types").find(options),
					client.collection("orientations").find(options),
					client.collection("progressios").find(options),
					client.collection("materials").find(options),
					client.collection("temporadas").find(options),
				]);

				setAccuracies(cast<Accuracy>(accuraciesRes.data).sort(sortBy("name")));
				setScopes(cast<Scope>(scopesRes.data).sort(sortBy("name")));
				setCountries(cast<Country>(countriesRes.data).sort(sortBy("name")));
				setDangerDegrees(cast<DangerDegree>(dangerDegreesRes.data).sort(sortBy("name")));
				setDesencadenants(cast<Desencadenant>(desencadenantsRes.data).sort(sortBy("name")));
				setCotas(cast<Cota>(cotasRes.data).sort(sortBy("name")));
				setActivitats(cast<Activitat>(activitatsRes.data).sort(sortBy("name")));
				setRegions(cast<Region>(regionsRes.data).sort(sortBy("name")));
				setMassis(cast<Massis>(massisRes.data).sort(sortBy("name")));
				setOrigens(cast<Origen>(origensRes.data).sort(sortBy("name")));
				setSnowType(cast<SnowType>(snowTypesRes.data).sort(sortBy("name")));
				setOrientation(cast<Orientation>(orientationsRes.data).sort(sortBy("name")));
				setProgression(cast<Progressio>(progressionRes.data).sort(sortBy("name")));
				setMaterial(cast<Material>(materialRes.data).sort(sortBy("name")));
				setTemporadas(cast<Season>(seasonsRes.data).sort(sortBy("name")));
			} catch (error) {
				console.error(error);
			} finally {
				setLoadingFields(false);
			}
		};

		trackPromise(getAllFields());
	}, []);

	const formik = useFormik<AccidentPost>({
		enableReinitialize: false,
		initialValues: {
			temporada: edit ? accident?.temporada?.id : undefined,
			date: edit ? accident?.date : undefined,
			place: edit ? accident?.place : undefined,
			damage: edit ? accident?.damage : undefined,
			observations: edit ? accident?.observations : undefined,
			link: edit ? accident?.link : undefined,
			members: edit ? accident?.members : undefined,
			triggers: edit ? accident?.triggers : undefined,
			dragged: edit ? accident?.dragged : undefined,
			damaged: edit ? accident?.damaged : undefined,
			dead: edit ? accident?.dead : undefined,
			scope: edit ? accident?.scope?.id : undefined,
			accuracy: edit ? accident?.accuracy?.id : undefined,
			country: edit ? accident?.country?.id : undefined,
			danger_degree: edit ? accident?.danger_degree?.id : undefined,
			desencadenant: edit ? accident?.desencadenant?.id : undefined,
			coordinates: {
				latitude: marker.latitude,
				longitude: marker.longitude,
			},
			size: edit ? accident?.size : undefined,
			cota: edit ? accident?.cota?.id : undefined,
			activitat: edit ? accident?.activitat?.id : undefined,
			region: edit ? accident?.region?.id : undefined,
			massi: edit ? accident?.massi?.id : undefined,
			origen: edit ? accident?.origen?.id : undefined,
			snow_type: edit ? accident?.snow_type?.id : undefined,
			orientation: edit ? accident?.orientation?.id : undefined,
			progressio: edit ? accident?.progressio?.id : undefined,
			material: edit ? accident?.material?.id : undefined,
		},
		validationSchema: Yup.object({
			temporada: Yup.string().required(t("required")),
		}),
		onSubmit: (values) => {
			const submitData = async () => {
				try {
					const client = getStrapiClient();

					const uploadFiles = async (files: (File | BEFile)[]) => {
						const realFiles = files.filter((f): f is File => f instanceof File);
						if (realFiles.length === 0) return [];
						const formData = new FormData();
						realFiles.forEach((file) => formData.append("files", file, file.name));

						const res = await client.fetch("upload", {
							method: "POST",
							body: formData,
						});
						if (!res.ok) throw new Error("Upload failed");
						const uploaded = await res.json();
						return (Array.isArray(uploaded) ? uploaded : [uploaded]).map((f) => f.id).filter(Boolean);
					};

					// UPLOAD FILES AND THEN UPLOAD ENTRY
					const newValues = values;
					newValues.date = moment(values.date).format("YYYY-MM-DD");
					if (newValues.coordinates) {
						newValues.coordinates.latitude = latitude;
						newValues.coordinates.longitude = longitude;
					}

					const normalizedData = Object.fromEntries(
						Object.entries(newValues).map(([key, value]) => {
							if (value === undefined || value === "") {
								return [key, null];
							} else {
								return [key, value];
							}
						}),
					);

					if (newValues.temporada) {
						// UPDATE ACCIDENT
						if (edit) {
							const existingGalleryIds = (accident?.gallery ?? []).map((f) => f.id);
							const existingDocIds = (accident?.doc ?? []).map((f) => f.id);

							const newGalleryIds = await uploadFiles(photos);
							const newDocIds = await uploadFiles(docs);

							const payload = {
								data: {
									...normalizedData,
									gallery: [...existingGalleryIds, ...newGalleryIds],
									doc: [...existingDocIds, ...newDocIds],
								},
							};

							await client.fetch(`accidents/${accident_id}`, {
								method: "PUT",
								headers: { "Content-Type": "application/json" },
								body: JSON.stringify(payload),
							});

							navigate("/");
						} else {
							// CREATE ACCIDENT
							const galleryIds = await uploadFiles(photos);
							const docIds = await uploadFiles(docs);

							const payload = {
								data: {
									...normalizedData,
									gallery: galleryIds,
									doc: docIds,
								},
							};

							await client.fetch("accidents", {
								method: "POST",
								headers: { "Content-Type": "application/json" },
								body: JSON.stringify(payload),
							});

							navigate("/");
						}
					} else {
						alert("La temporada és obligatòria");
					}
				} catch (error) {
					console.error(error);
				}
			};
			trackPromise(submitData());
		},
	});

	const MapEvents = () => {
		const _map = useMapEvents({
			click(e) {
				setMarker({ latitude: e.latlng.lat, longitude: e.latlng.lng });
			},
		});
		return null;
	};

	useEffect(() => {
		const fetchData = async () => {
			const client = getStrapiClient();
			let response = await client.collection("accidents").findOne(accident_id ?? "", { populate: "*" });
			const accident = response.data as Accident;
			formik.setValues({
				temporada: accident?.temporada?.id,
				date: accident?.date,
				place: accident?.place,
				damage: accident?.damage,
				observations: accident?.observations,
				link: accident?.link,
				members: accident?.members,
				triggers: accident?.triggers,
				dragged: accident?.dragged,
				damaged: accident?.damaged,
				dead: accident?.dead,
				scope: accident?.scope?.id,
				accuracy: accident?.accuracy?.id,
				country: accident?.country?.id,
				danger_degree: accident?.danger_degree?.id,
				desencadenant: accident?.desencadenant?.id,
				coordinates: {
					latitude: marker.latitude,
					longitude: marker.longitude,
				},
				size: accident?.size,
				cota: accident?.cota?.id,
				activitat: accident?.activitat?.id,
				region: accident?.region?.id,
				massi: accident?.massi?.id,
				origen: accident?.origen?.id,
				snow_type: accident?.snow_type?.id,
				orientation: accident?.orientation?.id,
				progressio: accident?.progressio?.id,
				material: accident?.material?.id,
			});
			setAccident(accident);
			setMarker({
				latitude: accident.coordinates.latitude,
				longitude: accident.coordinates.longitude,
			});

			setLatitude(accident.coordinates.latitude);
			setLongitude(accident.coordinates.longitude);

			response = await client.collection("accidents").findOne(accident_id ?? "", { populate: ["doc", "gallery"] });
			const accPopulated = response.data as Accident;
			setPhotos(accPopulated.gallery ?? []);
			setDocs(accPopulated.doc ?? []);
			setLoadingAccident(false);
		};

		if (edit && accident_id) {
			trackPromise(fetchData());
		}
	}, [accident_id, edit, formik, marker.latitude, marker.longitude]);

	const Skeleton = ({ className }: { className?: string }) => <div className={clsx("animate-pulse rounded-md bg-gray-200", className)} />;

	if (loadingFields || (edit && loadingAccident)) {
		return (
			<div className="space-y-4 p-4">
				<div className="flex items-center space-x-3">
					<Skeleton className="h-8 w-8 rounded-full" />
					<Skeleton className="h-6 w-48" />
				</div>
				<div className="grid gap-4 md:grid-cols-2">
					{Array.from({ length: 4 }).map((_, idx) => (
						<div key={idx} className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
							<Skeleton className="h-5 w-32" />
							<Skeleton className="h-4 w-24" />
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-4 w-1/2" />
							<Skeleton className="h-10 w-full" />
						</div>
					))}
				</div>
				<div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
					<Skeleton className="mb-3 h-6 w-32" />
					<Skeleton className="h-64 w-full" />
				</div>
			</div>
		);
	}

	return (
		<div className="p-4">
			<div className="flex items-center space-x-4 py-4">
				<div className="font-medium text-gray-900 text-xl">{edit ? "Editar accident" : "Nou accident"}</div>
			</div>
			<form className="space-y-6" onSubmit={formik.handleSubmit}>
				<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
					<div className="md:grid md:grid-cols-3 md:gap-6">
						<div className="md:col-span-1">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.form.date_scope.title")}</h3>
							<p className="mt-1 text-gray-500 text-sm">{t("accidents.form.date_scope.subtitle")}</p>
						</div>
						<div className="mt-5 md:col-span-2 md:mt-0">
							<div className="space-y-6">
								<div className="grid grid-cols-6 gap-6">
									<div className="col-span-6 sm:col-span-3">
										<div className="flex items-center justify-between">
											<label htmlFor="temporada" className="block text-left font-medium text-gray-700 text-sm">
												{t("accidents.models.season")}
											</label>
											{formik.errors.temporada && <div className="text-red-500">{formik.errors.temporada}</div>}
										</div>
										<select
											id="temporada"
											name="temporada"
											className={cn(
												"mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm",
												!formik.values.temporada && "bg-red-100",
											)}
											onChange={formik.handleChange}
											value={formik.values.temporada}
										>
											<option selected value="">
												{t("accidents.form.select.season")}
											</option>
											{temporadas.map((temp) => (
												<option key={temp.id} value={temp.id}>
													{temp.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="date" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.date")}
										</label>
										<div className="mt-1">
											<DatePicker
												value={formik.values.date ? moment(formik.values.date).toDate() : undefined}
												onChange={(date) => formik.setFieldValue("date", moment(date).toISOString())}
												className={clsx(!formik.values.date && "bg-red-100", "py-4.5")}
											/>
										</div>
									</div>

									<div className="col-span-6">
										<label htmlFor="scope" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.scope")}
										</label>
										<select
											id="scope"
											name="scope"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.scope}
										>
											<option selected value="">
												{t("accidents.form.select.scope")}
											</option>
											{scopes.map((scope) => (
												<option key={scope.id} value={scope.id}>
													{scope.name}
												</option>
											))}
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
					<div className="md:grid md:grid-cols-3 md:gap-6">
						<div className="md:col-span-1">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.form.geo.title")}</h3>
							<p className="mt-1 text-gray-500 text-sm">{t("accidents.form.geo.subtitle")}</p>
						</div>
						<div className="mt-5 md:col-span-2 md:mt-0">
							<div>
								<div className="grid grid-cols-6 gap-6">
									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="place" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.place")}
										</label>
										<input
											type="text"
											name="place"
											id="place"
											placeholder="Baqueira"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.place}
										/>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="country" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.country")}
										</label>
										<select
											id="country"
											name="country"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.country}
										>
											<option selected value="">
												{t("accidents.form.select.country")}
											</option>
											{countries.map((c) => (
												<option key={c.id} value={c.id} label={c.name} />
											))}
										</select>
									</div>

									<div className="col-span-6 sm:col-span-4">
										<label htmlFor="region" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.region")}
										</label>
										<select
											id="region"
											name="region"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.region}
										>
											<option selected value="">
												{t("accidents.form.select.region")}
											</option>
											{regions.map((region) => (
												<option key={region.id} value={region.id}>
													{region.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="massi" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.massis")}
										</label>
										<select
											id="massi"
											name="massi"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.massi}
										>
											<option selected value="">
												{t("accidents.form.select.massis")}
											</option>
											{massis.map((massis) => (
												<option key={massis.id} value={massis.id}>
													{massis.name}
												</option>
											))}
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
					<div className="md:grid md:grid-cols-3 md:gap-6">
						<div className="md:col-span-1">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.form.geo.title")}</h3>
							<p className="mt-1 text-gray-500 text-sm">{t("accidents.form.geo.subtitle")}</p>
						</div>
						<div className="mt-5 md:col-span-2 md:mt-0">
							<div className="space-y-6">
								<div className="grid grid-cols-6 gap-6">
									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="latitude" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.coordinates.lat")}
										</label>
										<input
											type="text"
											name="latitude"
											id="latitude"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={(e) => {
												setLatitude(Number(e.target.value));
												setMarker({ latitude: Number(e.target.value), longitude: marker.longitude });
											}}
											value={latitude}
										/>
									</div>
									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="longitude" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.coordinates.lng")}
										</label>
										<input
											type="text"
											name="longitude"
											id="longitude"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={(e) => {
												setLongitude(Number(e.target.value));
												setMarker({ latitude: marker.latitude, longitude: Number(e.target.value) });
											}}
											value={longitude}
										/>
									</div>
									{/* Map */}
									<div className="col-span-6 h-96">
										<MapContainer
											center={[marker.latitude, marker.longitude]}
											zoom={7}
											scrollWheelZoom={false}
											style={{ height: "100%", width: "100%" }}
										>
											<MapEvents />
											<LayersControl position="topright">
												{mapLayers.map((layer, _index) => {
													switch (layer.type) {
														case "wms":
															return (
																<LayersControl.BaseLayer key={layer.name} checked={layer.checked} name={layer.name}>
																	<WMSTileLayer
																		url={layer.url}
																		layers={layer.layer}
																		format={layer.format}
																		attribution={layer.attribution}
																	/>
																</LayersControl.BaseLayer>
															);
														case "tile":
															return (
																<LayersControl.BaseLayer key={layer.name} checked={layer.checked} name={layer.name}>
																	<TileLayer attribution={layer.attribution} url={layer.url} />
																</LayersControl.BaseLayer>
															);
														default:
															return <>Error</>;
													}
												})}
											</LayersControl>
											<MyMap />
											{marker && (
												<Marker key={`marker-${marker.latitude}`} position={[marker.latitude, marker.longitude]} />
											)}
										</MapContainer>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="orientation" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.orientation")}
										</label>
										<select
											id="orientation"
											name="orientation"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.orientation}
										>
											<option selected value="">
												{t("accidents.form.select.orientation")}
											</option>
											{orientation.map((orientations) => (
												<option key={orientations.id} value={orientations.id}>
													{orientations.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6 sm:col-span-4">
										<label htmlFor="progressio" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.progressio")}
										</label>
										<select
											id="progressio"
											name="progressio"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.progressio}
										>
											<option selected value="">
												{t("accidents.form.select.progressio")}
											</option>
											{progression.map((p) => (
												<option key={p.id} value={p.id}>
													{p.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="accuracy" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.accuracy")}
										</label>
										<select
											id="accuracy"
											name="accuracy"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.accuracy}
										>
											<option selected value="">
												{t("accidents.form.select.accuracy")}
											</option>
											{accuracies.map((accuracy) => (
												<option key={accuracy.id} value={accuracy.id}>
													{accuracy.name} ({accuracy.units.number} {accuracy.units.units})
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6">
										<label htmlFor="cota" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.cota")}
										</label>
										<select
											id="cota"
											name="cota"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.cota}
										>
											<option selected value="">
												{t("accidents.form.select.cota")}
											</option>
											{cotas.map((cota) => (
												<option key={cota.id} value={cota.id}>
													{cota.name}
												</option>
											))}
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
					<div className="md:grid md:grid-cols-3 md:gap-6">
						<div className="md:col-span-1">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.form.nivo.title")}</h3>
						</div>
						<div className="mt-5 md:col-span-2 md:mt-0">
							<div className="space-y-6">
								<div className="grid grid-cols-6 gap-6">
									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="origen" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.origen")}
										</label>
										<select
											id="origen"
											name="origen"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.origen}
										>
											<option selected value="">
												{t("accidents.form.select.origen")}
											</option>
											{origens.map((origen) => (
												<option key={origen.id} value={origen.id}>
													{origen.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="snow_type" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.snow_type")}
										</label>
										<select
											id="snow_type"
											name="snow_type"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.snow_type}
										>
											<option selected value="">
												{t("accidents.form.select.snow_type")}
											</option>
											{snowType.map((type) => (
												<option key={type.id} value={type.id}>
													{type.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6 sm:col-span-4">
										<label htmlFor="size" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.size")}
										</label>
										<input
											type="text"
											id="size"
											name="size"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.size}
										/>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="danger_degree" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.danger_degree")}
										</label>
										<select
											id="danger_degree"
											name="danger_degree"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.danger_degree}
										>
											<option selected value="">
												{t("accidents.form.select.danger_degree")}
											</option>
											{dangerDegrees.map((danger) => (
												<option key={danger.id} value={danger.id}>
													{danger.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6">
										<label htmlFor="desencadenant-address" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.desencadenant")}
										</label>
										<select
											id="desencadenant"
											name="desencadenant"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.desencadenant}
										>
											<option selected value="">
												{t("accidents.form.select.desencadenant")}
											</option>
											{desencadenants.map((desencadenant) => (
												<option key={desencadenant.id} value={desencadenant.id}>
													{desencadenant.name}
												</option>
											))}
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
					<div className="md:grid md:grid-cols-3 md:gap-6">
						<div className="md:col-span-1">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.form.details.title")}</h3>
						</div>
						<div className="mt-5 md:col-span-2 md:mt-0">
							<div className="space-y-6">
								<div className="grid grid-cols-6 gap-6">
									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="activitat" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.activitat")}
										</label>
										<select
											id="activitat"
											name="activitat"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.activitat}
										>
											<option selected value="">
												{t("accidents.form.select.activitat")}
											</option>
											{activitats.map((activitat) => (
												<option key={activitat.id} value={activitat.id}>
													{activitat.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="material" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.material")}
										</label>
										<select
											id="material"
											name="material"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.material}
										>
											<option selected value="">
												{t("accidents.form.select.material")}
											</option>
											{material.map((m) => (
												<option key={m.id} value={m.id}>
													{m.name}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-6">
										<label htmlFor="damage" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.damage")}
										</label>
										<textarea
											id="damage"
											name="damage"
											rows={3}
											className="block w-full max-w-lg rounded-md border border-gray-300 shadow-xs focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.damage}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
					<div className="md:grid md:grid-cols-3 md:gap-6">
						<div className="md:col-span-1">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.form.stats.title")}</h3>
						</div>
						<div className="mt-5 md:col-span-2 md:mt-0">
							<div className="space-y-6">
								<div className="grid grid-cols-6 gap-6">
									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="members" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.members")}
										</label>
										<input
											type="number"
											name="members"
											id="members"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.members}
										/>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="triggers" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.triggers")}
										</label>
										<input
											type="number"
											name="triggers"
											id="triggers"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.triggers}
										/>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="dragged" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.dragged")}
										</label>
										<input
											type="number"
											name="dragged"
											id="dragged"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.dragged}
										/>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="damaged" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.damaged")}
										</label>
										<input
											type="number"
											name="damaged"
											id="damaged"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.damaged}
										/>
									</div>

									<div className="col-span-6 sm:col-span-3">
										<label htmlFor="dead" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.dead")}
										</label>
										<input
											type="number"
											name="dead"
											id="dead"
											className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.dead}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
					<div className="md:grid md:grid-cols-3 md:gap-6">
						<div className="md:col-span-1">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.form.multimedia.title")}</h3>
							<p className="mt-1 text-gray-500 text-sm">{t("accidents.form.multimedia.subtitle")}</p>
						</div>
						<div className="mt-5 md:col-span-2 md:mt-0">
							<div className="space-y-6">
								<div className="grid grid-cols-6 gap-6">
									{/* Media management */}
									<div className="col-span-6">
										<label htmlFor="first-name" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.photos")}
										</label>
										{photos.length > 0 ? (
											<div className="col-span-6">
												<ul className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200">
													{photos.map((photo, _index) => (
														<li
															key={"id" in photo ? photo.id : photo.name}
															className="flex items-center justify-between py-3 pr-4 pl-3 text-sm"
														>
															<div className="flex w-0 flex-1 items-center">
																<PaperClipIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
																<span className="ml-2 w-0 flex-1 truncate">{photo.name}</span>
															</div>
															<div className="ml-4 shrink-0">
																<button
																	type="button"
																	className="font-medium text-blue-600 hover:text-blue-500"
																	onClick={() => {
																		const deletePhoto = async () => {
																			try {
																				const client = getStrapiClient();
																				await client.fetch(`upload/files/${(photo as BEFile).id}`, {
																					method: "DELETE",
																				});
																			} catch (error) {
																				console.error(error);
																			}
																		};
																		setPhotos(photos.filter((photo2) => photo2 !== photo));
																		if (edit) {
																			trackPromise(deletePhoto());
																		}
																	}}
																>
																	{t("accidents.form.buttons.delete")}
																</button>
															</div>
														</li>
													))}
												</ul>
											</div>
										) : (
											<div className="mt-2 sm:col-span-2 sm:mt-0">
												<div className="flex max-w-lg justify-center rounded-md border-2 border-gray-300 border-dashed px-6 pt-5 pb-6">
													<div className="space-y-1 text-center">
														<svg
															className="mx-auto h-12 w-12 text-gray-400"
															stroke="currentColor"
															fill="none"
															viewBox="0 0 48 48"
															aria-hidden="true"
														>
															<path
																d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
																strokeWidth={2}
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														</svg>
														<div className="flex text-gray-600 text-sm">
															<label
																htmlFor="file-upload"
																className="relative cursor-pointer rounded-md bg-white font-medium text-blue-600 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 hover:text-blue-500"
															>
																<span>{t("accidents.form.buttons.add_photo")}</span>
																<input
																	id="file-upload"
																	name="file-upload"
																	type="file"
																	className="sr-only"
																	onChange={(e) => {
																		setPhotos((p) => [...p, ...(e.target.files ?? [])]);
																	}}
																/>
															</label>
														</div>
													</div>
												</div>
											</div>
										)}
									</div>

									<div className="col-span-6">
										<label htmlFor="first-name" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.docs")}
										</label>
										{docs.length > 0 ? (
											<div className="col-span-6">
												<div className="my-2 flex w-full flex-row">
													<label
														htmlFor="docs-upload"
														className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 font-medium text-base text-white shadow-xs hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto sm:text-sm"
													>
														<span>{t("accidents.form.buttons.add_docs")}</span>
														<input
															id="docs-upload"
															name="docs-upload"
															type="file"
															className="sr-only"
															onChange={(e) => {
																setDocs((d) => [...d, ...(e.target.files ?? [])]);
															}}
														/>
													</label>
												</div>
												<ul className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200">
													{docs.map((doc) => (
														<li
															key={"id" in doc ? doc.id : doc.name}
															className="flex items-center justify-between py-3 pr-4 pl-3 text-sm"
														>
															<div className="flex w-0 flex-1 items-center">
																<PaperClipIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
																<span className="ml-2 w-0 flex-1 truncate">{doc.name}</span>
															</div>
															<div className="ml-4 shrink-0">
																<button
																	type="button"
																	className="font-medium text-blue-600 hover:text-blue-500"
																	onClick={() => {
																		const deleteDocs = async () => {
																			try {
																				const client = getStrapiClient();
																				await client.fetch(`upload/files/${(doc as BEFile).id}`, {
																					method: "DELETE",
																				});
																			} catch (error) {
																				console.error(error);
																			}
																		};
																		setDocs(docs.filter((doc2) => doc2 !== doc));
																		if (edit) {
																			trackPromise(deleteDocs());
																		}
																	}}
																>
																	{t("accidents.form.buttons.delete")}
																</button>
															</div>
														</li>
													))}
												</ul>
											</div>
										) : (
											<div className="mt-2 sm:col-span-2 sm:mt-0">
												<div className="flex max-w-lg justify-center rounded-md border-2 border-gray-300 border-dashed px-6 pt-5 pb-6">
													<div className="space-y-1 text-center">
														<svg
															className="mx-auto h-12 w-12 text-gray-400"
															stroke="currentColor"
															fill="none"
															viewBox="0 0 48 48"
															aria-hidden="true"
														>
															<path
																d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
																strokeWidth={2}
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														</svg>
														<div className="flex text-gray-600 text-sm">
															<label
																htmlFor="docs-upload"
																className="relative cursor-pointer rounded-md bg-white font-medium text-blue-600 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 hover:text-blue-500"
															>
																<span>{t("accidents.form.buttons.add_docs")}</span>
																<input
																	id="docs-upload"
																	name="docs-upload"
																	type="file"
																	className="sr-only"
																	onChange={(e) => {
																		setDocs((d) => [...d, ...(e.target.files ?? [])]);
																	}}
																/>
															</label>
														</div>
													</div>
												</div>
											</div>
										)}
									</div>
									<div className="col-span-6 sm:col-span-4">
										<label htmlFor="link" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.link")}
										</label>
										<div className="flex max-w-lg rounded-md shadow-xs">
											<span className="inline-flex items-center rounded-l-md border border-gray-300 border-r-0 bg-gray-50 px-3 text-gray-500 sm:text-sm">
												https://
											</span>
											<input
												type="text"
												name="link"
												id="link"
												className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
												onChange={formik.handleChange}
												value={formik.values.link}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6">
					<div className="md:grid md:grid-cols-3 md:gap-6">
						<div className="md:col-span-1">
							<h3 className="font-medium text-gray-900 text-lg leading-6">{t("accidents.form.other")}</h3>
						</div>
						<div className="mt-5 md:col-span-2 md:mt-0">
							<div className="space-y-6">
								<div className="grid grid-cols-6 gap-6">
									<div className="col-span-6 sm:col-span-4">
										<label htmlFor="observations" className="block font-medium text-gray-700 text-sm">
											{t("accidents.models.observations")}
										</label>
										<textarea
											id="observations"
											name="observations"
											rows={3}
											className="block w-full max-w-lg rounded-md border border-gray-300 shadow-xs focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
											onChange={formik.handleChange}
											value={formik.values.observations}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="flex justify-end">
					<button
						type="button"
						className="rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 text-sm shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
						onClick={() => navigate("/")}
					>
						{t("accidents.form.buttons.cancel")}
					</button>
					<button
						type="submit"
						className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 font-medium text-sm text-white shadow-xs hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
					>
						{edit ? t("accidents.form.buttons.edit") : t("accidents.form.buttons.add")}
					</button>
				</div>
			</form>
		</div>
	);
}
